// RadioOS-managed sign-in (documentation/protocol.md): e-mail + password, then - for accounts that
// require it - a second factor (TOTP enrolment or verification, or a recovery code).
//
// The challenge id is kept in memory only (component state); the browser binding is an HttpOnly
// cookie the server sets. The TOTP setup secret and the recovery codes exist only in memory while
// the screen shows them; nothing here touches localStorage, sessionStorage, IndexedDB or
// document.cookie. A refresh therefore restarts the sign-in (the server keeps no unfinished
// enrolment usable).

export type LoginResult =
  | { kind: 'authenticated' }
  | { kind: 'mfa'; challenge: string; next: 'verify' | 'enroll' }
  | { kind: 'invalid'; message: string }
  | { kind: 'rate_limited' }
  | { kind: 'unavailable' };

/** Outcome of a second-factor step. */
export type StepError = 'invalid_code' | 'sign_in_again' | 'rate_limited' | 'unavailable' | 'acknowledgement_required';

export interface Enrollment {
  secret: string;
  otpauth_uri: string;
  issuer: string;
  account: string;
  algorithm: string;
  digits: number;
  period: number;
}

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;
const defaultFetch: Fetcher = (i, n) => fetch(i, n);

const post = (fetcher: Fetcher, path: string, body: unknown) =>
  fetcher(path, {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

async function json(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

const FAILED = 'Sign-in failed. Check the e-mail address and password, or try again later.';

/** A second-factor step that continues a sign-in started elsewhere (Microsoft/Google step-up). */
export interface PendingStep {
  challenge: string;
  next: 'verify' | 'enroll';
}

/**
 * Takes the RadioOS second-factor step the server attached to a Microsoft/Google sign-in that
 * carried no MFA evidence (`/#mfa=<challenge>&next=verify|enroll`) and removes it from the address
 * at once (memory only from here; the challenge is useless without the HttpOnly browser cookie).
 */
export function takePendingStep(loc: Pick<Location, 'hash' | 'pathname' | 'search'> = window.location,
  replace: (url: string) => void = (url) => window.history.replaceState(null, '', url)): PendingStep | null {
  if (!loc.hash || !loc.hash.startsWith('#mfa=')) return null;
  const q = new URLSearchParams(loc.hash.slice(1));
  const challenge = q.get('mfa') || '';
  replace(loc.pathname + loc.search);
  if (!/^[A-Za-z0-9_-]{20,128}$/.test(challenge)) return null;
  return { challenge, next: q.get('next') === 'enroll' ? 'enroll' : 'verify' };
}

export async function managedLogin(email: string, password: string, fetcher: Fetcher = defaultFetch): Promise<LoginResult> {
  try {
    const res = await post(fetcher, '/api/auth/password/login', { email, password });
    const body = await json(res);
    if (res.ok && body.status === 'authenticated') return { kind: 'authenticated' };
    if (res.ok && body.status === 'mfa_required' && typeof body.challenge === 'string') {
      return { kind: 'mfa', challenge: body.challenge, next: body.next === 'enroll' ? 'enroll' : 'verify' };
    }
    if (res.status === 401) return { kind: 'invalid', message: typeof body.message === 'string' ? body.message : FAILED };
    if (res.status === 429) return { kind: 'rate_limited' };
    return { kind: 'unavailable' };
  } catch {
    return { kind: 'unavailable' };
  }
}

function stepError(status: number, body: Record<string, unknown>): StepError {
  if (status === 401 && body.error === 'invalid_code') return 'invalid_code';
  if (status === 401) return 'sign_in_again';
  if (status === 400 && body.error === 'acknowledgement_required') return 'acknowledgement_required';
  if (status === 429) return 'rate_limited';
  return 'unavailable';
}

async function step<T>(fetcher: Fetcher, path: string, body: unknown, ok: (b: Record<string, unknown>) => T | null): Promise<{ ok: T } | { error: StepError }> {
  try {
    const res = await post(fetcher, path, body);
    const b = await json(res);
    if (res.ok) {
      const v = ok(b);
      if (v !== null) return { ok: v };
      return { error: 'unavailable' };
    }
    return { error: stepError(res.status, b) };
  } catch {
    return { error: 'unavailable' };
  }
}

export const startEnrollment = (challenge: string, fetcher: Fetcher = defaultFetch) =>
  step<Enrollment>(fetcher, '/api/auth/mfa/enroll/start', { challenge }, (b) =>
    typeof b.secret === 'string' && typeof b.otpauth_uri === 'string' ? (b as unknown as Enrollment) : null);

export const confirmEnrollment = (challenge: string, code: string, fetcher: Fetcher = defaultFetch) =>
  step<string[]>(fetcher, '/api/auth/mfa/enroll/confirm', { challenge, code }, (b) =>
    Array.isArray(b.recovery_codes) ? (b.recovery_codes as string[]) : null);

export const finishEnrollment = (challenge: string, fetcher: Fetcher = defaultFetch) =>
  step<true>(fetcher, '/api/auth/mfa/enroll/finish', { challenge, acknowledged: true }, (b) => (b.status === 'authenticated' ? true : null));

export const verifyCode = (challenge: string, code: string, fetcher: Fetcher = defaultFetch) =>
  step<true>(fetcher, '/api/auth/mfa/verify', { challenge, code }, (b) => (b.status === 'authenticated' ? true : null));

export const submitRecoveryCode = (challenge: string, code: string, fetcher: Fetcher = defaultFetch) =>
  step<true>(fetcher, '/api/auth/mfa/recovery', { challenge, code }, (b) => (b.status === 'authenticated' ? true : null));

export const STEP_MESSAGE: Record<StepError, string> = {
  invalid_code: 'The code is not correct. Check your authenticator app and try again.',
  sign_in_again: 'This sign-in has expired or can no longer be completed. Please sign in again.',
  rate_limited: 'Too many attempts. Please try again later.',
  unavailable: 'Sign-in is temporarily unavailable. Please try again later.',
  acknowledgement_required: 'Confirm that you have saved the recovery codes.',
};

/** The manual setup key in readable groups of four. */
export function groupKey(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/** Plain-text content of the recovery-code download (built client-side from memory only). */
export function recoveryCodesText(codes: string[], account: string): string {
  return [
    'RadioOS recovery codes' + (account ? ` for ${account}` : ''),
    'Each code can be used once to sign in when your authenticator app is not available.',
    '',
    ...codes,
    '',
  ].join('\n');
}
